<template>
  <div class="profile-menu" @mouseleave="$emit('closed', true)">
    <div class="profile-menu__header">
      <div class="profile-menu__user-wrapper">
        <img
          class="profile-menu__image"
          :src="currentAvatar"
          alt="Icon representing the user"
        />
        <div class="profile-menu__user-info">
          <p class="profile-menu__name">{{ userFullName }}</p>
          <p class="profile-menu__email">{{ userEmail }}</p>
        </div>
      </div>
      <div class="profile-menu__profile" @click="userAction('profile')">
        View Profile
        <ViewProfile />
      </div>
      <div
        v-if="isImpersonating && isAuthenticated"
        class="profile-menu__impersonation"
      >
        <button
          @click="stopImpersonating()"
          class="profile-menu__impersonation--btn"
        >
          Stop Impersonating
        </button>
      </div>
    </div>
    <div class="profile-menu__list-wrapper">
      <ul class="profile-menu__list">
        <li
          v-for="option in options"
          :key="`option-${option.name}`"
          class="profile-menu__list-item"
        >
          <button
            v-if="isOptionShown(option.action)"
            class="profile-menu__option"
            @click="userAction(option.action)"
          >
            {{ option.name }}
          </button>
        </li>
        <template v-if="isAdmin">
          <li
            v-for="option in adminOptions"
            :key="`option-${option.name}`"
            class="profile-menu__list-item"
            @click="$emit('closed', true)"
          >
            <router-link
              class="profile-menu__option"
              :to="{ name: 'admin', params: { mode: option.action } }"
              >Manage User {{ option.name }}</router-link
            >
          </li>
        </template>
        <li class="profile-menu__list-item">
          <button
            class="profile-menu__option profile-menu__option--signout"
            @click="logout"
          >
            <SignOut />Log Out
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import SignOut from '@/assets/icons/navigation/sign-out.svg'
import ViewProfile from '@/assets/icons/navigation/view-profile.svg'

export default {
  name: 'ProfileMenu',
  components: {
    SignOut: { ...SignOut, name: 'SignOut' },
    ViewProfile: { ...ViewProfile, name: 'ViewProfile' },
  },
  props: {
    profile: {
      type: Object,
      required: false,
    },
    currentAvatar: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      options: [
        {
          name: 'Active Courses',
          action: 'active-courses',
        },
        {
          name: 'Bookmarks',
          action: 'bookmarks',
        },
        {
          name: 'Bootcamps',
          action: 'bootcamps',
        },
        {
          name: 'Analytics',
          action: 'analytics',
        },
        {
          name: 'Rack Rentals',
          action: 'rentals',
        },
        {
          name: 'Subscriptions',
          action: 'subscriptions',
        },
        {
          name: 'Workbooks',
          action: 'workbooks',
        },
        {
          name: 'Legacy Dashboard',
          action: 'members',
        },
      ],
      adminOptions: [
        {
          name: 'Impersonation',
          action: 'impersonate-user',
        },
        {
          name: 'Suspension',
          action: 'manage-user-suspension',
        },
        {
          name: 'Password',
          action: 'manage-user-password',
        },
        {
          name: 'Confirmation',
          action: 'manage-user-confirmation',
        },
      ],
    }
  },
  computed: {
    ...mapState({
      isImpersonating: state => state.admin.isImpersonating,
      isAdmin: state => state.auth.isAdmin,
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    ...mapGetters({
      currentPasses: 'subscriptions/currentPasses',
      isWorkbooksIncluded: 'subscriptions/isWorkbooksIncluded',
    }),
    membersUrl() {
      return process.env.VUE_APP_MEMBERS_URL
    },
    userFullName() {
      const { first_name: firstName, last_name: lastName } = this.profile
      return `${firstName} ${lastName}`
    },
    userEmail() {
      const { email } = this.profile
      return email
    },
    isWorkbooksShown() {
      if (this.isAdmin) {
        return true
      }
      return this.isWorkbooksIncluded
    },
  },
  methods: {
    userAction(action) {
      if (action === 'workbooks') {
        this.$emit('toggleWorkbooks')
      } else if (action === 'members' || action === 'rentals') {
        window.open(`https://${this.membersUrl}/`)
      } else if (action === 'bootcamps') {
        this.$router.push({ name: 'bootcamps' })
      } else {
        this.$router.push({
          name: 'user-dashboard',
          params: { mode: action },
        })
      }
      this.$emit('closed', true)
    },
    isOptionShown(option) {
      if (option === 'workbooks') {
        return this.isWorkbooksShown
      }
      return true
    },
    logout() {
      this.$router.push({ name: 'logout' })
      this.$emit('closed', true)
    },
    async stopImpersonating() {
      this.$store.dispatch('auth/setIsAuthLoading', true)
      this.$store.dispatch('admin/isImpersonating', false)
      await this.$store.dispatch('admin/cancelImpersonation')
      if (this.$route.name !== 'home') this.$router.push({ name: 'home' })
      this.$store.dispatch('auth/setIsAuthLoading', false)
    },
  },
}
</script>

<style lang="scss" scoped>
.profile-menu {
  background: var(--dark-gray);
  padding: 15px 0;
  min-width: 340px;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 301;
  height: auto;
  &__header {
    border-bottom: 1px solid var(--light-gray);
    overflow: hidden;
    padding: 0 35px;
  }
  &__user-wrapper {
    display: flex;
    align-items: center;
  }
  &__image {
    width: 70px;
    height: 70px;
    border-radius: 100%;
    margin: 0 15px 0 0;
  }
  &__user-info {
    color: var(--white);
  }
  &__name {
    font-size: (18 / 16) * 1rem;
    margin: 0;
    color: var(--white);
  }
  &__email {
    font-size: (13 / 16) * 1rem;
    margin: 0;
  }
  &__profile {
    background: var(--light-gray);
    text-transform: uppercase;
    color: var(--white);
    padding: 5px 25px;
    border-radius: 20px;
    margin: 10px 0 25px 0;
    float: right;
    display: flex;
    align-items: center;
    transition: background 0.15s ease;
    cursor: pointer;
    &:hover {
      background: #323333;
    }
    svg {
      margin: 0 0 0 15px;
    }
  }
  &__impersonation {
    font-size: (18 / 16) * 1rem;
    margin: 0;
    color: var(--white);
    &--lead {
      color: var(--orange);
    }
    &--btn {
      font-size: 0.8rem;
      background: var(--red);
      text-transform: uppercase;
      color: var(--white);
      padding: 5px 25px;
      border-radius: 20px;
      border: none;
      margin: 10px 0 25px 0;
      transition: background 0.15s ease;
      cursor: pointer;
      &:hover {
        background: var(--green);
      }
    }
  }
  &__list-wrapper {
    position: relative;
    height: 100%;
    width: 100%;
    @media screen and (max-height: 700px) {
      height: 100vh;
      overflow-y: auto;
    }
  }
  &__list {
    width: 100%;
    height: 100%;
    list-style-type: none;
    padding: 25px 35px 10px 35px;
    margin: 0;
    @media screen and (max-height: 700px) {
      position: absolute;
      height: 350px;
      overflow-y: auto;
    }
  }
  &__list-item {
    margin: 0 0 15px 0;
    &:last-child {
      margin: 0;
    }
  }
  &__option {
    border: none;
    appearance: none;
    font-size: (18 / 16) * 1rem;
    color: var(--white);
    background: none;
    padding: 0;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
    &--signout {
      display: flex;
      align-items: center;
    }
    svg {
      width: 25px;
      height: auto;
      fill: #777;
      margin: 0 8px 0 0;
    }
  }
}
</style>
